import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  DollarSign,
  FileText,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  Download,
  CreditCard,
  Check,
  X,
  ShoppingCart,
  Upload,
  Paperclip,
  Eye,
} from "lucide-react";


const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-700",
  partial: "bg-yellow-100 text-yellow-700",
  overdue: "bg-red-100 text-red-700",
  paid: "bg-green-100 text-green-700",
};

export default function MyFinancePage() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [uploadingPop, setUploadingPop] = useState(null); // invoice_number being uploaded
  const [popModalInvoice, setPopModalInvoice] = useState(null); // For PoP upload modal
  const fileInputRef = useRef(null);
  const [summary, setSummary] = useState({
    total_outstanding: 0,
    total_overdue: 0,
    invoice_count: 0,
    overdue_count: 0,
  });

  useEffect(() => {
    fetchInvoices();
    
    // Check if returning from Stripe payment
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (paymentStatus === "success" && sessionId) {
      pollPaymentStatus(sessionId);
      // Clean up URL
      setSearchParams({});
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment was cancelled");
      setSearchParams({});
    }
  }, [token]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/customer-portal/my-invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data.invoices || []);
      setSummary({
        total_outstanding: response.data.total_outstanding || 0,
        total_overdue: response.data.total_overdue || 0,
        invoice_count: response.data.invoices?.length || 0,
        overdue_count: response.data.invoices?.filter(i => i.status === 'overdue').length || 0,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast.info("Payment status check timed out. Please check your email for confirmation.");
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API_URL}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Thank you for your payment.");
        setCheckingPayment(false);
        setSelectedInvoices([]);
        fetchInvoices(); // Refresh invoices
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error("Error checking payment status. Please contact support.");
        setCheckingPayment(false);
      }
    }
  };

  const handlePaySelected = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("Please select invoices to pay");
      return;
    }

    setProcessingPayment(true);

    try {
      const response = await axios.post(
        `${API_URL}/payments/create-checkout`,
        {
          invoice_ids: selectedInvoices,
          origin_url: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setProcessingPayment(false);
    }
  };

  // Proof of Payment upload handler - supports multiple invoices
  const handlePopUpload = async (invoiceNumbers, file) => {
    if (!file) return;
    
    const invoiceList = Array.isArray(invoiceNumbers) ? invoiceNumbers : [invoiceNumbers];
    setUploadingPop(invoiceList.join(","));
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("invoice_numbers", invoiceList.join(","));
    
    try {
      const response = await axios.post(
        `${API_URL}/invoices/upload-pop`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      toast.success(response.data.message || "Proof of payment uploaded!");
      setPopModalInvoice(null);
      setSelectedInvoices([]); // Clear selection after upload
      fetchInvoices(); // Refresh to show updated status
    } catch (error) {
      console.error("PoP upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload proof of payment");
    } finally {
      setUploadingPop(null);
    }
  };

  // Open PoP modal - can be single invoice or selected invoices
  const openPopUploadModal = (invoice) => {
    if (invoice) {
      // Single invoice mode
      setPopModalInvoice({ 
        invoices: [invoice],
        invoice_numbers: [invoice.invoice_number || invoice.id],
        total: invoice.outstanding || invoice.amount || 0,
        currency: invoice.currency || "EUR"
      });
    } else if (selectedInvoices.length > 0) {
      // Multi-invoice mode (from selection)
      const selectedInvs = invoices.filter(inv => 
        selectedInvoices.includes(inv.invoice_number || inv.id)
      );
      const total = selectedInvs.reduce((sum, inv) => sum + (inv.outstanding || inv.amount || 0), 0);
      setPopModalInvoice({
        invoices: selectedInvs,
        invoice_numbers: selectedInvoices,
        total: total,
        currency: selectedInvs[0]?.currency || "EUR"
      });
    }
  };

  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const selectAllUnpaid = () => {
    const unpaidIds = invoices
      .filter((inv) => inv.status !== "paid" && (inv.outstanding || inv.amount) > 0)
      .map((inv) => inv.invoice_number || inv.id);
    setSelectedInvoices(unpaidIds);
  };

  const getSelectedTotal = () => {
    return invoices
      .filter((inv) => selectedInvoices.includes(inv.invoice_number || inv.id))
      .reduce((sum, inv) => sum + (inv.outstanding || inv.amount || 0), 0);
  };

  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">My Finance</h1>
        <p className="text-slate-600 mb-8">View your invoices and make payments</p>

        {/* Payment Status Check Overlay */}
        {checkingPayment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-sm mx-4 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Processing Payment</h3>
              <p className="text-slate-600">Please wait while we confirm your payment...</p>
            </div>
          </div>
        )}

        {/* PoP Upload Modal - Supports Multiple Invoices */}
        <AnimatePresence>
          {popModalInvoice && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setPopModalInvoice(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Upload Proof of Payment</h3>
                  <button
                    onClick={() => setPopModalInvoice(null)}
                    className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Invoice(s) Summary */}
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-slate-600 mb-2">
                    {popModalInvoice.invoices?.length > 1 ? `${popModalInvoice.invoices.length} Invoices Selected` : "Invoice"}
                  </p>
                  
                  {popModalInvoice.invoices?.length > 1 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {popModalInvoice.invoices.map((inv) => (
                        <div key={inv.invoice_number} className="flex justify-between text-sm bg-white rounded p-2">
                          <span className="font-medium">{inv.invoice_number}</span>
                          <span>{formatCurrency(inv.outstanding || inv.amount, inv.currency)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-bold text-lg">{popModalInvoice.invoice_numbers?.[0]}</p>
                  )}
                  
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Total Amount:</span>
                      <span className="text-xl font-bold text-blue-600">
                        {formatCurrency(popModalInvoice.total, popModalInvoice.currency)}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-slate-600 mb-4">
                  Upload a screenshot or PDF of your bank transfer confirmation. Our accounting team will review and confirm your payment.
                </p>

                <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm">
                  <p className="text-blue-800">
                    <strong>Note:</strong> Make sure your transfer reference includes the invoice number(s) for faster processing.
                  </p>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handlePopUpload(popModalInvoice.invoice_numbers, e.target.files[0]);
                    }
                  }}
                />

                <Button
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!!uploadingPop}
                >
                  {uploadingPop ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Select File (PDF, PNG, JPEG)
                </Button>

                <p className="text-xs text-slate-400 mt-3 text-center">
                  Max file size: 10MB. Email will be sent to acc@europacific.com
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Outstanding</p>
                <p className="text-xl font-bold">{formatCurrency(summary.total_outstanding)}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Overdue</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(summary.total_overdue)}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Invoices</p>
                <p className="text-xl font-bold">{summary.invoice_count}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Overdue Invoices</p>
                <p className="text-xl font-bold text-yellow-600">{summary.overdue_count}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Payment Action Bar */}
        {selectedInvoices.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-600 text-white rounded-xl p-4 mb-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <ShoppingCart className="h-5 w-5" />
              <div>
                <p className="font-semibold">{selectedInvoices.length} invoice(s) selected</p>
                <p className="text-blue-100 text-sm">
                  Total: {formatCurrency(getSelectedTotal())}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="ghost"
                className="text-white hover:bg-blue-700"
                onClick={() => setSelectedInvoices([])}
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
              <Button
                variant="outline"
                className="border-white/50 text-white hover:bg-blue-700 bg-transparent"
                onClick={() => openPopUploadModal(null)}
                data-testid="upload-pop-selected-btn"
              >
                <Upload className="h-4 w-4 mr-1" />
                Upload PoP
              </Button>
              <Button
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={handlePaySelected}
                disabled={processingPayment}
                data-testid="pay-selected-btn"
              >
                {processingPayment ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Pay Now
              </Button>
            </div>
          </motion.div>
        )}

        {/* Invoices Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border shadow-sm overflow-hidden"
        >
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Invoices</h2>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllUnpaid}
                disabled={invoices.filter(i => i.status !== 'paid').length === 0}
              >
                <Check className="h-4 w-4 mr-1" />
                Select All Unpaid
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Download Statement
              </Button>
            </div>
          </div>
          
          {invoices.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg">No invoices found</p>
              <p className="text-sm">Your invoices will appear here</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="w-12 p-3"></th>
                  <th className="text-left p-3 text-xs font-medium text-slate-500">Invoice #</th>
                  <th className="text-left p-3 text-xs font-medium text-slate-500">PF Reference</th>
                  <th className="text-center p-3 text-xs font-medium text-slate-500">Invoice Date</th>
                  <th className="text-center p-3 text-xs font-medium text-slate-500">Due Date</th>
                  <th className="text-right p-3 text-xs font-medium text-slate-500">Amount</th>
                  <th className="text-right p-3 text-xs font-medium text-slate-500">Outstanding</th>
                  <th className="text-center p-3 text-xs font-medium text-slate-500">Status</th>
                  <th className="text-center p-3 text-xs font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const invoiceId = invoice.invoice_number || invoice.id;
                  const isSelected = selectedInvoices.includes(invoiceId);
                  const isPaid = invoice.status === "paid";
                  const outstanding = invoice.outstanding || invoice.amount || 0;
                  const hasPopSubmitted = invoice.pop_submitted;
                  
                  return (
                    <tr 
                      key={invoiceId} 
                      className={`border-b transition-colors ${
                        isSelected ? "bg-blue-50" : "hover:bg-slate-50"
                      } ${isPaid ? "opacity-60" : ""}`}
                    >
                      <td className="p-3 text-center">
                        {!isPaid && outstanding > 0 && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleInvoiceSelection(invoiceId)}
                            data-testid={`select-invoice-${invoiceId}`}
                          />
                        )}
                        {isPaid && (
                          <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                        )}
                      </td>
                      <td className="p-3 font-medium">{invoice.invoice_number}</td>
                      <td className="p-3 text-slate-600">{invoice.pf_number || "-"}</td>
                      <td className="p-3 text-center text-sm">{invoice.invoice_date?.slice(0, 10) || "-"}</td>
                      <td className="p-3 text-center text-sm">{invoice.due_date?.slice(0, 10) || "-"}</td>
                      <td className="p-3 text-right">{formatCurrency(invoice.amount, invoice.currency)}</td>
                      <td className="p-3 text-right font-medium">
                        {formatCurrency(outstanding, invoice.currency)}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[invoice.status] || "bg-gray-100"}`}>
                          {invoice.status || "open"}
                        </span>
                        {hasPopSubmitted && (
                          <span className="ml-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            PoP
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {!isPaid && outstanding > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => openPopUploadModal(invoice)}
                            title="Upload Proof of Payment"
                            data-testid={`upload-pop-${invoiceId}`}
                          >
                            <Upload className="h-3.5 w-3.5 mr-1" />
                            {hasPopSubmitted ? "Re-upload" : "PoP"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </motion.div>

        {/* Payment Methods Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center">
                <CreditCard className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Pay by Card</h3>
                <p className="text-blue-100 text-sm">Secure online payment</p>
              </div>
            </div>
            <p className="text-sm text-blue-100 mb-4">
              Select invoices above and click "Pay Now" to pay securely with your credit or debit card via Stripe.
            </p>
            <div className="flex items-center gap-2 text-xs text-blue-200">
              <Check className="h-3 w-3" /> Visa
              <Check className="h-3 w-3 ml-2" /> Mastercard
              <Check className="h-3 w-3 ml-2" /> Amex
            </div>
          </div>

          <div className="bg-slate-100 rounded-xl p-6">
            <h3 className="font-semibold text-slate-800 mb-2">Bank Transfer</h3>
            <p className="text-sm text-slate-600 mb-4">
              For bank transfer payments, please use the following details and include your invoice number as reference:
            </p>
            <div className="bg-white rounded-lg p-4 text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Bank:</span>
                <span className="font-medium">Nova KBM d.d.</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">IBAN:</span>
                <span className="font-medium">SI56 0510 0801 5368 837</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">BIC/SWIFT:</span>
                <span className="font-medium">KBMASI2X</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              For payment inquiries: <a href="mailto:acc@europacific.com" className="text-blue-600 underline">acc@europacific.com</a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
